import { Controller, useForm } from "react-hook-form";
import { useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { adaptBatchItem } from "@/features/cosignature/helpers/adapters";
import { Badge2 } from "@/ui2/atoms/Badge";
import type { BatchCosignItem } from "@/features/cosignature/types";
import { Button2 } from "@/components/UI2/atoms/Button";
import { ModalControlled2, ModalDivider } from "@/components/UI2/molecules/Modal";
import { TextField2 } from "@/components/UI2/molecules/TextField";
import { useBatchRejectDocuments } from "@/features/cosignature/api/mutations/useBatchRejectDocuments";

import { batchRejectSchema, type BatchRejectFormValues } from "./modal.schemas";

type BatchRejectNotesModalProps = {
  isOpen: boolean;
  items: BatchCosignItem[];
  onClose: () => void;
  onSuccess?: VoidFunction;
};

export function BatchRejectNotesModal({
  isOpen,
  items,
  onClose,
  onSuccess,
}: BatchRejectNotesModalProps) {
  const { control, formState, handleSubmit, reset } = useForm<BatchRejectFormValues>({
    defaultValues: { rejectionReason: "" },
    mode: "onChange",
    resolver: zodResolver(batchRejectSchema),
  });

  const { isPending, mutate } = useBatchRejectDocuments({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    ({ rejectionReason }: BatchRejectFormValues) =>
      mutate({
        items: adaptBatchItem(items),
        rejection_reason: rejectionReason,
      }),
    [items, mutate],
  );

  return (
    <ModalControlled2 isOpen={isOpen} onOpenChange={handleClose} title="Reject Notes">
      <div className="tw:flex tw:flex-col tw:gap-2">
        <p className="tw:text-text-secondary">
          You are about to reject{" "}
          <b>
            {items.length} {items.length === 1 ? "note" : "notes"}
          </b>{" "}
          and return {items.length === 1 ? "it" : "them"} to{" "}
          {items.length === 1 ? "its author" : "their authors"} for revision.
        </p>

        <p className="tw:text-text-secondary">
          Please provide a detailed comment explaining why this note is being rejected. Authors will
          receive this feedback and the note will be returned to
          <Badge2 appearance="outline" className="tw:mx-ch" size="xs" variant="bright-purple">
            Draft
          </Badge2>
          status.
        </p>
      </div>

      <form className="tw:contents" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="rejectionReason"
          render={({ field, fieldState }) => (
            <TextField2
              errorMessage={fieldState.error?.message}
              hasStrongLabel
              isInvalid={!!fieldState.error}
              label="Rejection reason"
              mode="textarea"
              onChange={field.onChange}
              placeholder="Explain what needs to be corrected or added…"
              textAreaProps={{ className: "tw:min-h-24" }}
              value={field.value}
            />
          )}
        />

        <ModalDivider />

        <div className="tw:flex tw:justify-end tw:gap-3">
          <Button2 onPress={handleClose} variant="secondary">
            Cancel
          </Button2>

          <Button2
            isDisabled={!formState.isValid}
            isLoading={isPending}
            type="submit"
            variant="destructive-secondary"
          >
            Reject Notes
          </Button2>
        </div>
      </form>
    </ModalControlled2>
  );
}
